import { Head, Link, usePage } from "@inertiajs/react"
import { Plus } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { DataRow, DataTable } from "@/components/ui/data-table"
import { TaskFormDialog } from "@/components/tasks/TaskFormDialog"
import { TaskList } from "@/components/tasks/TaskList"
import type { TaskRecord } from "@/components/tasks/types"
import type { PageProps } from "@/types/inertia"

type FieldDef = {
  readonly key: string
  readonly label: string
  readonly type: "text" | "number" | "select" | "date" | "boolean"
  readonly options?: string[]
}

type ContactDetail = {
  readonly id: number
  readonly email: string
  readonly first_name: string | null
  readonly last_name: string | null
  readonly company: string | null
  readonly title: string | null
  readonly phone: string | null
  readonly linkedin_url: string | null
  readonly replies_count: number
  readonly open_tasks_count: number
  readonly notes: string
  readonly icp_fit: string | null
  readonly last_reply_sentiment: string | null
  readonly source_campaign_name: string | null
}

type ReplySummary = {
  readonly id: number
  readonly lead_name: string
  readonly reply_subject: string
  readonly received_at: string | null
  readonly status: string
  readonly pipeline_stage: string
  readonly client: {
    readonly id: number
    readonly display_name: string
    readonly color_hex: string
  } | null
}

type OpportunitySummary = {
  readonly id: number
  readonly name: string
  readonly coarse_stage: string
  readonly status: string
  readonly client: { readonly display_name: string } | null
}

type ContactShowProps = {
  readonly contact: ContactDetail
  readonly replies: readonly ReplySummary[]
  readonly tasks: readonly TaskRecord[]
  readonly account: { readonly id: number; readonly name: string; readonly domain: string } | null
  readonly opportunities: readonly OpportunitySummary[]
  readonly custom_fields: Record<string, unknown>
  readonly field_definitions: readonly FieldDef[]
}

export default function ContactShow({ contact, replies, tasks, account, opportunities, custom_fields, field_definitions }: ContactShowProps) {
  const { props } = usePage<PageProps<ContactShowProps>>()
  const name = contactName(contact)
  const description = `Contact details, reply threads, and tasks for ${name}.`

  return (
    <>
      <Head title={name}>
        <meta name="description" content={description} />
        <meta property="og:title" content={name} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell wide>
        <div className="flex flex-col gap-6">
          <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1>{name}</h1>
              {contact.company && <p className="mt-2">{contact.company}</p>}
            </div>
            <Button asChild variant="secondary">
              <Link href={`/contacts/${contact.id}/edit`}>Edit</Link>
            </Button>
          </header>

          {props.flash.notice && (
            <div className="rounded-md border border-accent bg-accent-faded px-4 py-3 text-sm text-accent-display">
              {props.flash.notice}
            </div>
          )}

          <section className="rounded-lg border border-hairline bg-page px-6">
            <DataTable>
              <DataRow title="Email">{contact.email}</DataRow>
              <DataRow title="Company">{contact.company || "—"}</DataRow>
              {contact.title && <DataRow title="Title">{contact.title}</DataRow>}
              {contact.phone && <DataRow title="Phone">{contact.phone}</DataRow>}
              {contact.linkedin_url && (
                <DataRow title="LinkedIn">
                  <a href={contact.linkedin_url} target="_blank" rel="noopener noreferrer" className="no-underline hover:underline">
                    {contact.linkedin_url}
                  </a>
                </DataRow>
              )}
              {contact.notes && <DataRow title="Notes">{contact.notes}</DataRow>}
            </DataTable>
          </section>

          {account && (
            <section className="rounded-lg border border-hairline bg-page px-6">
              <DataTable>
                <DataRow title="Account">
                  <Link href={`/crm/accounts/${account.id}`} className="text-accent-display underline">
                    {account.name}{account.domain ? ` (${account.domain})` : ""}
                  </Link>
                </DataRow>
              </DataTable>
            </section>
          )}

          {(contact.icp_fit || contact.last_reply_sentiment || contact.source_campaign_name) && (
            <section className="rounded-lg border border-hairline bg-page px-6">
              <DataTable>
                <DataRow title="ICP fit">{contact.icp_fit}</DataRow>
                <DataRow title="Last reply sentiment">{contact.last_reply_sentiment}</DataRow>
                <DataRow title="Source campaign">{contact.source_campaign_name}</DataRow>
              </DataTable>
            </section>
          )}

          {opportunities.length > 0 && (
            <section className="flex flex-col gap-4">
              <h2>Opportunities</h2>
              <div className="overflow-hidden rounded-lg border border-hairline bg-page">
                <div className="divide-y divide-hairline">
                  {opportunities.map((opp) => (
                    <div key={opp.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-ink-display">{opp.name}</div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-ink-muted">
                          {opp.client && <span>{opp.client.display_name}</span>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Badge tone="accent">{opp.coarse_stage}</Badge>
                        <Badge tone="muted">{opp.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {field_definitions && field_definitions.length > 0 && Object.keys(custom_fields || {}).length > 0 && (
            <section className="rounded-lg border border-hairline bg-page px-6">
              <h2 className="sr-only">Custom fields</h2>
              <DataTable>
                {field_definitions.filter(f => custom_fields[f.key] != null).map((field) => (
                  <DataRow key={field.key} title={field.label}>
                    {field.type === "boolean" ? (custom_fields[field.key] ? "Yes" : "No") : String(custom_fields[field.key])}
                  </DataRow>
                ))}
              </DataTable>
            </section>
          )}

          {Object.keys(custom_fields || {}).filter(k => !(field_definitions || []).some(f => f.key === k)).length > 0 && (
            <section className="rounded-lg border border-hairline bg-page px-6">
              <h2>Enriched data</h2>
              <DataTable>
                {Object.keys(custom_fields || {}).filter(k => !(field_definitions || []).some(f => f.key === k)).map((key) => (
                  <DataRow key={key} title={key}>
                    {String(custom_fields[key])}
                  </DataRow>
                ))}
              </DataTable>
            </section>
          )}

          <section className="flex flex-col gap-4">
            <h2>Threads</h2>
            {replies.length > 0 ? (
              <div className="overflow-hidden rounded-lg border border-hairline bg-page">
                <div className="divide-y divide-hairline">
                  {replies.map((reply) => (
                    <Link
                      key={reply.id}
                      href={`/inbox?selected=${reply.id}`}
                      className="grid gap-2 px-4 py-4 no-underline hover:bg-surface sm:grid-cols-[1fr_auto] sm:items-center sm:gap-4"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-ink-display">
                          {reply.reply_subject || reply.lead_name}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-ink-muted">
                          {reply.client && (
                            <span className="inline-flex items-center gap-1.5">
                              <span
                                className="h-2 w-2 rounded-full border border-hairline"
                                style={{ backgroundColor: reply.client.color_hex }}
                              />
                              {reply.client.display_name}
                            </span>
                          )}
                          <span>{formatDateTime(reply.received_at)}</span>
                        </div>
                      </div>
                      <div className="flex gap-2 sm:justify-end">
                        <Badge tone="muted">{reply.status}</Badge>
                        <Badge tone="accent">{reply.pipeline_stage}</Badge>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-ink-muted">No reply threads for this contact yet.</p>
            )}
          </section>

          <section className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h2>Tasks</h2>
              <TaskFormDialog
                contactId={contact.id}
                trigger={
                  <Button type="button" size="sm">
                    <Plus className="h-4 w-4" />
                    Add task
                  </Button>
                }
              />
            </div>
            <TaskList tasks={tasks} emptyText="No tasks for this contact yet." />
          </section>
        </div>
      </AppShell>
    </>
  )
}

function contactName(contact: ContactDetail) {
  const name = `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim()
  return name || contact.email
}

function formatDateTime(value: string | null) {
  if (!value) return "Unknown"
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value))
}
